/**
 * Strings the portal needs that COPY_DECK.md does not have (research-screens.md §(d)). Every one is a
 * placeholder to be replaced by deck copy; keep them minimal and neutral. Nothing else in apps/portal may
 * introduce user-visible prose.
 */
export const placeholders = {
  app: {
    title: 'Kiddie Kredit', // PLACEHOLDER — not in COPY_DECK (browser tab title; O06 has "Kiddie Kredit" alone)
  },
  signIn: {
    // `title` was 'Staff sign in', a placeholder. Evan ruled the screen's own copy on 2026-09-22 — the eyebrow
    // "STAFF SIGN IN" and the heading "Sign in to your club" — so it lives in deck.signIn and not here.
    password: 'Password', // PLACEHOLDER — not in COPY_DECK (P02 has "Password (8+ characters)" for account creation)
    signOut: 'Sign out', // PLACEHOLDER — not in COPY_DECK
    orgPicker: 'Organization', // PLACEHOLDER — not in COPY_DECK (a11y label on the org select)
  },
  sheet: {
    close: 'Close', // PLACEHOLDER — not in COPY_DECK
    menu: 'Menu', // PLACEHOLDER — not in COPY_DECK (a11y label on the ≡ button)
  },
  deny: {
    title: 'Reason', // PLACEHOLDER — not in COPY_DECK
    notePlaceholder: 'Optional note', // PLACEHOLDER — not in COPY_DECK
    confirm: 'Deny', // PLACEHOLDER — not in COPY_DECK
    cancel: 'Cancel', // PLACEHOLDER — not in COPY_DECK
    approveA11y: 'Approve', // PLACEHOLDER — not in COPY_DECK (a11y label on the ✓ square)
    denyA11y: 'Deny', // PLACEHOLDER — not in COPY_DECK (a11y label on the × square)
  },
  proof: {
    noteTitle: 'Note', // PLACEHOLDER — not in COPY_DECK
    photoTitle: 'Photo', // PLACEHOLDER — not in COPY_DECK
    photoAlt: 'Proof photo', // PLACEHOLDER — not in COPY_DECK
    /** Evan's words for it (2026-09-16), not yet in COPY_DECK: the photo existed and is past its 30 days. */
    photoExpired: 'This photo has expired', // PLACEHOLDER — not in COPY_DECK
  },
  empty: {
    approvals: 'Nothing waiting', // PLACEHOLDER — not in COPY_DECK
    waiting: 'No one waiting', // PLACEHOLDER — not in COPY_DECK
    loading: 'Loading…', // PLACEHOLDER — not in COPY_DECK
  },
  invite: {
    // the wrong-account state is ruled copy now and lives in deck.states.inviteWrongAccount
    accepting: 'Accepting your invite…', // PLACEHOLDER — not in COPY_DECK
  },
  redemptions: {
    /**
     * PLACEHOLDER — not in COPY_DECK. The deck draws only "CODE KK-4821 · VALID"; every other RedemptionStatus
     * takes the status word in the same eyebrow position.
     */
    statusWord: {
      fulfilled: 'USED',
      denied: 'DENIED',
      expired: 'EXPIRED',
      cancelled: 'CANCELLED',
    } as const,
    codeStatus: (code: string, word: string) => `CODE ${code} · ${word}`, // PLACEHOLDER — not in COPY_DECK
    codeField: 'Code', // PLACEHOLDER — not in COPY_DECK (a11y label on the code field)
    avatarAlt: (firstName: string) => firstName, // PLACEHOLDER — not in COPY_DECK (a11y label on discs)
  },
  /**
   * OPEN_QUESTIONS 18l: /assignments has no board frame. The page title is the deck's own rail word
   * (`deck.nav.assignments`) and the deck draws no count template or subtitle for the list, so neither is
   * invented here; what is left below is what design still owes the screen.
   */
  assignments: {
    newAssignment: 'New assignment', // PLACEHOLDER — not in COPY_DECK (the O05 "New group" shape; O03's "NEW ASSIGNMENT" is an eyebrow, not a button)
    // a published assignment by its dates; "Published" alone stayed on one weeks after it ended (the 23 Oct audit, T2-4)
    upcoming: 'Upcoming', // approved 23 Oct (Mike), T3-12
    running: 'Running', // approved 23 Oct (Mike), T3-12
    ended: 'Ended', // approved 23 Oct (Mike), T3-12
    columns: {
      assignment: 'ASSIGNMENT', // PLACEHOLDER — not in COPY_DECK
      tasks: 'TASKS', // PLACEHOLDER — not in COPY_DECK as a column head ("TASKS · 3" is the builder's section label)
      children: 'CHILDREN', // PLACEHOLDER — not in COPY_DECK
      status: 'STATUS', // PLACEHOLDER — not in COPY_DECK
    },
    empty: 'No assignments yet', // PLACEHOLDER — not in COPY_DECK (worded like groups.empty / staff.empty)
  },
  builder: {
    titleField: 'Title', // PLACEHOLDER — not in COPY_DECK (a11y label on the title field)
    startDate: 'Start', // PLACEHOLDER — not in COPY_DECK (a11y label on the first WHEN field)
    endDate: 'End', // PLACEHOLDER — not in COPY_DECK (a11y label on the second WHEN field)
    taskTitle: 'Task', // PLACEHOLDER — not in COPY_DECK (task title field placeholder)
    fewerReps: 'Fewer', // PLACEHOLDER — not in COPY_DECK (a11y label on the − stepper button)
    moreReps: 'More', // PLACEHOLDER — not in COPY_DECK (a11y label on the + stepper button)
    remove: 'Remove', // PLACEHOLDER — not in COPY_DECK (task / reward remove)
    minScore: 'Minimum score', // PLACEHOLDER — not in COPY_DECK (a11y label on the score number field)
    saved: 'Saved', // PLACEHOLDER — not in COPY_DECK (after a successful save)
    readOnly: 'Published · read only', // PLACEHOLDER — not in COPY_DECK (a published assignment cannot be edited)
  },
  /**
   * Club billing (DECISIONS 90). English approved 23 Oct (Evan and Mike); shown only while CLUB_BILLING is open.
   * The prices match the Organizations page.
   */
  billing: {
    label: 'Billing', // approved 23 Oct (Evan and Mike)
    monthly: '$5 per child per month', // approved 23 Oct (Evan and Mike)
    yearly: '$4 per child per month, paid yearly', // approved 23 Oct (Evan and Mike)
    thisMonth: (children: number, dollars: string) =>
      `${children} ${children === 1 ? 'child' : 'children'} connected this month · about ${dollars}`, // approved 23 Oct (Evan and Mike)
    setUp: 'Set up billing to invite families. You pay only for children whose families join.', // approved 23 Oct (Evan and Mike)
    pastDue: "Your last payment didn't go through. Update your card to keep your club running.", // approved 23 Oct (Evan and Mike)
    paused: "Your club is paused. Families can't see your tasks or rewards until billing is up to date.", // approved 23 Oct (Evan and Mike)
    comped: "Your club isn't charged.", // approved 23 Oct (Evan and Mike)
    sponsored: (sponsor: string | undefined, children: number) =>
      `${sponsor ?? 'A sponsor'} pays for ${children} ${children === 1 ? 'child' : 'children'}.`, // approved 23 Oct (Evan and Mike)
    priceLock: (date: string) => `Your price is locked until ${date}.`, // approved 23 Oct (Evan and Mike)
    payMonthly: 'Pay Monthly', // approved 23 Oct (Evan and Mike)
    payYearly: 'Pay Yearly', // approved 23 Oct (Evan and Mike)
    manage: 'Manage Billing', // approved 23 Oct (Evan and Mike)
    // DECISIONS 92 (21 Oct): the banner over every screen, the deletion date and the Club Terms
    setUpButton: 'Set Up Billing', // approved 23 Oct (Evan and Mike)
    deletesOn: (date: string) => `If it stays unpaid, your club's data is deleted on ${date}.`, // approved 23 Oct (Evan and Mike)
    termsLine: 'By paying you agree to the Club Terms.', // approved 23 Oct (Evan and Mike)
    termsLink: 'Club Terms', // approved 23 Oct (Evan and Mike)
    // the 23 Oct audit (T1-1, T3-3, DECISIONS 93.2): a join code a parent can't use yet is shown dimmed, with why
    codeNeedsBilling: 'This code starts working once billing is set up.', // approved 23 Oct (Mike)
    codePaused: 'This code is paused until billing is up to date.', // approved 23 Oct (Mike)
    // the 23 Oct audit (T3-9): a group admin on a paused club, who can't change billing, gets a next step
    pausedAskAdmin: "Your club is paused. Ask your club's admin to update billing.", // approved 23 Oct (Mike)
  },
  settings: {
    // PLACEHOLDER — not in COPY_DECK. O08 draws no save button (the board shows the fields at rest), so the
    // label for the one that appears once a field is dirty is ours until the deck has one.
    save: 'Save',
  },
  groups: {
    orgAdminOnly: 'Org admins only', // PLACEHOLDER — not in COPY_DECK (a group admin on /groups)
    empty: 'No groups yet', // PLACEHOLDER — not in COPY_DECK
    nameField: 'Name', // PLACEHOLDER — not in COPY_DECK (the New group sheet)
    branchField: 'Branch', // PLACEHOLDER — not in COPY_DECK (a11y label on the branch select)
    noBranch: 'No branch', // PLACEHOLDER — not in COPY_DECK (the branch select's empty option)
  },
  staff: {
    invite: 'Invite', // PLACEHOLDER — not in COPY_DECK (the invite form's submit)
    role: 'Role', // PLACEHOLDER — not in COPY_DECK (a11y label on the role select)
    columns: {
      name: 'NAME', // PLACEHOLDER — not in COPY_DECK
      role: 'ROLE', // PLACEHOLDER — not in COPY_DECK
      sent: 'SENT', // PLACEHOLDER — not in COPY_DECK
    },
    pending: 'Pending', // PLACEHOLDER — not in COPY_DECK (the invites table's status word)
    devToken: 'dev only', // PLACEHOLDER — not in COPY_DECK (label on the read-only accept-token field)
    empty: 'No staff yet', // PLACEHOLDER — not in COPY_DECK
  },
} as const;
